import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Stack,
  Tooltip,
  Fab
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as ExitFullscreenIcon
} from '@mui/icons-material';
import StreamViewer from './StreamViewer';

/**
 * StreamGrid Component
 * Displays multiple video streams in a CSS grid layout (like DefectSummarySection)
 */
const StreamGrid = ({ 
  devices = [], 
  loading = false, 
  onRefresh,
  error = null 
}) => {
  // Filter devices that have stream URLs
  const streamingDevices = useMemo(() => {
    return devices.filter(device => device.stream_url);
  }, [devices]);

  const handleRefreshAll = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  // Show error state
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        {onRefresh && (
          <IconButton 
            onClick={handleRefreshAll}
            color="primary"
            size="large"
          >
            <RefreshIcon />
          </IconButton>
        )}
      </Box>
    );
  }

  // Show empty state
  if (streamingDevices.length === 0) {
    return (
      <Box sx={{ 
        textAlign: 'center', 
        py: 6,
        border: '2px dashed',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper'
      }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No Live Streams Available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          There are no devices with configured stream URLs in this view.
        </Typography>
        {onRefresh && (
          <IconButton 
            onClick={handleRefreshAll}
            color="primary"
            size="large"
          >
            <RefreshIcon />
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', minHeight: '400px' }}>
      {/* Controls Header */}
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center" 
        sx={{ mb: 2, px: 1 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h6" color="primary">
            Live Streams ({streamingDevices.length})
          </Typography>
        </Stack>
        
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh All Streams">
            <IconButton onClick={handleRefreshAll} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      
      {/* Auto-refresh indicator */}
      {loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Refreshing streams...
        </Alert>
      )}
      
      {/* Stream Grid - Using CSS Grid like DefectSummarySection */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(3, 1fr)', 
            lg: 'repeat(4, 1fr)',
            xl: 'repeat(4, 1fr)'  // Maximum 4 per row
          },
          gap: 2,
          '& > *': {
            height: '100%',  // Ensure equal heights
          }
        }}
      >
        {streamingDevices.map((device) => (
          <Box key={device.id}>
            <StreamViewer
              device={device}
              maxHeight={320}  // Increased height for better viewing
              showControls={true}
              showDeviceInfo={true}
              onRefresh={onRefresh}
            />
          </Box>
        ))}
      </Box>
      
      {/* Floating Refresh Button (for mobile) */}
      <Fab
        color="primary"
        size="medium"
        onClick={handleRefreshAll}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          display: { xs: 'flex', md: 'none' }, // Only show on mobile
          zIndex: 1000
        }}
      >
        <RefreshIcon />
      </Fab>
    </Box>
  );
};

export default StreamGrid;